/**
 * Calendar arithmetic for the mortgage schedules. Pure, and never touches a `Date` component.
 *
 * ============================================================================
 * WHY "MONTHS ELAPSED" NEEDED WRITING DOWN
 * ============================================================================
 *
 * Every figure this module produces is a function of `m`, and `m` is an integer that STEPS. On the
 * day it steps, the household's stated debt changes. "Months since the start date" has at least
 * three defensible readings — calendar months touched, whole months completed, and days ÷ 30.44 —
 * and they disagree by one for most of every month. Left to whichever date helper someone reached
 * for first, the same mortgage shows two balances on two pages.
 *
 *     m = (evalY − startY) × 12 + (evalM − startM) − (evalD < startD ? 1 : 0)
 *
 * Completed whole months. `m < 0` means the part has not started; `m >= durationMonths` means it is
 * repaid.
 *
 * THE MONTH-END CASE IS ACCEPTED, NOT SOLVED. A part starting 31 January has `m = 0` on 28 February
 * and `m = 1` on 31 March, because 28 < 31. Under a "same day next month, clamped to month end"
 * reading it would be `m = 1` on 28 February. The completed-months rule is one day later at worst,
 * it is monotonic, and it never runs ahead of reality — which is the right direction to err when the
 * number is a debt.
 *
 * ============================================================================
 * WHY NOTHING HERE BUILDS A `Date` AND READS A COMPONENT OFF IT
 * ============================================================================
 *
 * `purchase_date`, `valuation_date` and `start_date` are all `date` columns, and the generated
 * Kysely type maps `date` and `timestamptz` to the same alias — so the compiler distinguishes
 * neither and the driver hands back a `Date` for both. A `Date` built from a bare calendar date sits
 * at UTC midnight, and `getMonth()` on a process running behind UTC returns the PREVIOUS month.
 *
 * In savings that moved a balance a month earlier on a chart. Here it would change `m`, and
 * therefore what the household is told they still owe. So the parsing below reads the string.
 */

interface CalendarParts {
  year: number;
  month: number;
  day: number;
}

/** `YYYY-MM-DD` → its three numbers. No `Date` is constructed. */
function partsOf(iso: string): CalendarParts {
  const [year, month, day] = iso.split("-").map(Number) as [number, number, number];
  return { year, month, day };
}

/**
 * The month a mortgage part's payment schedule actually begins: the FIRST OF THE MONTH AFTER drawdown.
 *
 * ==========================================================================================
 * WHY THE SCHEDULE DOES NOT BEGIN ON THE START DATE
 * ==========================================================================================
 *
 * Because that is not how a Dutch mortgage is paid, and this household's own statements say so. A part
 * drawn on 6 August 2025 makes its first annuity payment on 1 September 2025; the few days between
 * drawdown and month end are billed separately as interest and are not an instalment. Every subsequent
 * payment falls on the first of a month.
 *
 * The consequence of anchoring on the drawdown date instead is one payment of drift, permanently. Checked
 * against three real parts on 10 August 2026, where the provider charges interest on the balance after
 * ELEVEN payments and a drawdown-anchored schedule has made twelve:
 *
 *     part                      ours (drawdown)   the provider
 *     121,426 @ 3.19% / 322mo        315.06           315.71
 *     134,770 @ 4.23% / 360mo        467.03           467.71
 *      75,135 @ 3.19% / 359mo        195.68           196.02
 *
 * Wrong in the direction that understates what the household owes, which is the direction that matters.
 *
 * ==========================================================================================
 * IT IS DERIVED RATHER THAN ASKED FOR
 * ==========================================================================================
 *
 * The member enters what their statement says — "van 6-8-2025" — and this works out the rest. The
 * alternative was a second field for the first payment month, which is a question a mortgage statement
 * does not answer directly and which a member would therefore have to reason about. Deriving it means a
 * transcription error is the only error available.
 *
 * A part drawn ON the first of a month is not a special case: drawn 1 August, the first payment is still
 * 1 September, because a payment is made in arrears for the month it covers.
 */
export function scheduleAnchor(startDate: string): string {
  const { year, month } = partsOf(startDate);
  // December rolls to January of the next year. Month numbers here are 1-based, never a `Date` index.
  const anchorYear = month === 12 ? year + 1 : year;
  const anchorMonth = month === 12 ? 1 : month + 1;
  return `${anchorYear}-${String(anchorMonth).padStart(2, "0")}-01`;
}

/**
 * How many scheduled payments a part has made by `evaluatedOn` — the `m` every schedule figure is a
 * function of.
 *
 * Whole months completed since the SCHEDULE ANCHOR, not since the start date. Negative before the first
 * payment falls due, which includes the whole of the drawdown month: a part drawn on 6 August has made no
 * payment on 20 August and produces no line, which is correct rather than an omission — its first
 * instalment is a fortnight away.
 */
export function paymentsMade(startDate: string, evaluatedOn: string): number {
  return elapsedMonths(scheduleAnchor(startDate), evaluatedOn);
}

/**
 * The term in months, from a drawdown date and the final payment date a statement quotes.
 *
 * A mortgage statement says "van 6-8-2025 tot 30-6-2052" and never says "322 months". Asking a member to
 * convert one into the other is asking for the error that is hardest to notice: a part entered as 360
 * months instead of 322 pays €524.46 where the bank takes €561.72, and nothing about the figure looks
 * wrong. So the member enters both dates and this does the arithmetic.
 *
 * COUNTED FROM THE SCHEDULE ANCHOR, AND INCLUSIVE OF THE FINAL MONTH — the first payment falls in the
 * month after drawdown and the last falls in the end month, so both ends count. Verified against three
 * real parts, where it is the only rule that reproduces all three terms:
 *
 *     drawdown 2025-08-06, ends 2052-06-30  ->  Sept 2025 … June 2052  ->  322
 *     drawdown 2025-08-06, ends 2055-08-31  ->  Sept 2025 … Aug 2055   ->  360
 *     drawdown 2025-08-06, ends 2055-07-31  ->  Sept 2025 … July 2055  ->  359
 *
 * The DAY of the end date is deliberately ignored. Statements quote the last day of a month (30-6, 31-8,
 * 31-7) and one quotes a 30th where the month has 31 days; what identifies the final instalment is its
 * month, and reading the day would make 30-6 and 1-7 differ by a payment for no reason a lender recognises.
 */
export function durationMonthsBetween(startDate: string, endDate: string): number {
  const anchor = partsOf(scheduleAnchor(startDate));
  const end = partsOf(endDate);
  return (end.year - anchor.year) * 12 + (end.month - anchor.month) + 1;
}

/**
 * Whole months completed between `start` and `evaluatedOn`. Negative if the start is in the future.
 *
 * Both arguments are `YYYY-MM-DD`. For a mortgage schedule use `paymentsMade`, which anchors this to the
 * month payments actually begin; this function is the raw calendar arithmetic underneath it and is also
 * what `getProperty` reports as "months elapsed since drawdown".
 */
export function elapsedMonths(start: string, evaluatedOn: string): number {
  const from = partsOf(start);
  const to = partsOf(evaluatedOn);
  const wholeMonths = (to.year - from.year) * 12 + (to.month - from.month);
  return to.day < from.day ? wholeMonths - 1 : wholeMonths;
}

/**
 * UTC midnight for a calendar date — the ONE place in this context a date becomes a `Date`.
 *
 * `Valuation` carries an `asOf: Date`, so a derived figure needs one to be constructed. Built with
 * `Date.UTC` and never read back with a local-time accessor, which is the same discipline
 * `AsOfDate.toUtcDate()` applies in savings.
 */
export function utcDateOf(iso: string): Date {
  const { year, month, day } = partsOf(iso);
  return new Date(Date.UTC(year, month - 1, day));
}
